import { formatFare, formatWholeFare } from '@/lib/c2c/format'
import { isUsableRate } from '@/lib/currency/convert'
import { CANONICAL_CURRENCY, type DisplayCurrency } from '@/lib/currency/map'
import type {
    CalendarDate,
    Clock,
    Count,
    Country,
    Distance,
    Duration,
    Instant,
    List,
    Money,
    Month,
    Rating,
    Weekday,
} from '@/lib/facts/values'
import { dateFromIso, monthName, weekdayName } from '@/i18n/calendar'
import { DEFAULT_DISPLAY_TIMEZONE } from '@/i18n/dates'
import { localeFormatter } from '@/i18n/format'
import { countryName } from '@/i18n/places'
import type { Locale } from '@/i18n/routing'

import type { Presentation } from './variables'

/** Bound to the page's locale, rendered currency and rate, so `select` hands a component
 *  figures already formatted. A kind with presentations takes one as its second argument,
 *  the same closed menu copy writes as the ICU style (`./variables` `PRESENTATIONS`); the
 *  first of the menu is the default. */
export interface ValueFormatters {
    count(value: Count): string
    duration(value: Duration, presentation?: Presentation<'duration'>): string
    /** To at most one decimal by default; `whole` in whole units. */
    distance(value: Distance, presentation?: Presentation<'distance'>): string
    rating(value: Rating): string
    weekday(value: Weekday, presentation?: Presentation<'weekday'>): string
    month(value: Month, presentation?: Presentation<'month'>): string
    clock(value: Clock): string
    date(value: CalendarDate, presentation?: Presentation<'date'>): string
    instant(value: Instant, presentation?: Presentation<'instant'>): string
    country(value: Country): string
    list(value: List): string
    /** Without a presentation, a canonical amount converts to the rendered currency and any
     *  other states itself; `whole` the same in whole units; `native` states the amount in
     *  its own currency with the rendered-currency equivalent in parentheses when the
     *  currencies differ. A fixed amount renders native whatever the presentation. */
    money(value: Money, presentation?: Presentation<'money'>): string
}

const KILOMETRES_PER_MILE = 1.609344
const MINUTES_PER_DAY = 24 * 60

/** The regions that sign road distance in miles. By region, not language: a Spanish page in
 *  the United States reads miles, an English page in Canada kilometres. */
const MILE_REGIONS = new Set(['US', 'GB'])

const measuresRoadsInMiles = (locale: Locale) =>
    MILE_REGIONS.has(new Intl.Locale(locale).maximize().region ?? '')

export interface FormatInputs {
    readonly locale: Locale
    /** The currency the page's amounts are stated in — `renderedCurrency` of the
     *  selected currency and the rate, so a missing rate leaves it canonical. */
    readonly currency: DisplayCurrency
    /** Rendered-currency units per canonical unit; `undefined` states amounts canonical. */
    readonly rate: number | undefined
    /** The page type's display zone, which an instant is dated in. */
    readonly timeZone: string
}

/** Bound once per page, so every value of a kind formats by the same rule wherever it
 *  appears. */
export function createValueFormatters({
    locale,
    currency,
    rate,
    timeZone,
}: FormatInputs): ValueFormatters {
    const formatter = localeFormatter(locale)
    const rendered = { money: { locale, currency }, rate }
    const ownCurrency = (value: Money) => ({
        money: { locale, currency: value.currency as DisplayCurrency },
        rate: undefined,
    })
    // A canonical amount converts; any other states itself, since the rate only
    // converts from the canonical currency.
    const fareDisplay = (value: Money) =>
        value.currency === CANONICAL_CURRENCY ? rendered : ownCurrency(value)
    const convertible = (value: Money) =>
        value.currency === CANONICAL_CURRENCY &&
        currency !== CANONICAL_CURRENCY &&
        isUsableRate(rate)

    return {
        count: (value) => formatter.number(value),
        duration: (value, presentation = 'short') => {
            const whole = Math.round(value)
            const parts = [
                [Math.floor(whole / MINUTES_PER_DAY), 'day'],
                [Math.floor((whole % MINUTES_PER_DAY) / 60), 'hour'],
                [whole % 60, 'minute'],
            ] as const
            const stated = parts
                .filter(([amount, unit]) => amount > 0 || (unit === 'minute' && whole === 0))
                .map(([amount, unit]) =>
                    formatter.number(amount, { style: 'unit', unit, unitDisplay: presentation }),
                )
            return new Intl.ListFormat(
                locale,
                presentation === 'long'
                    ? { type: 'conjunction', style: 'long' }
                    : { type: 'unit', style: 'short' },
            ).format(stated)
        },
        distance: (value, presentation) => {
            const miles = measuresRoadsInMiles(locale)
            return formatter.number(miles ? value / KILOMETRES_PER_MILE : value, {
                style: 'unit',
                unit: miles ? 'mile' : 'kilometer',
                unitDisplay: 'long',
                maximumFractionDigits: presentation === 'whole' ? 0 : 1,
            })
        },
        rating: (value) => formatter.number(value, { maximumFractionDigits: 1 }),
        weekday: (value, presentation = 'long') => weekdayName(value, locale, presentation) ?? '',
        month: (value, presentation = 'long') => monthName(value, locale, presentation) ?? '',
        clock: (value) => {
            const [hours, minutes] = value.split(':').map(Number)
            // Pinned at UTC on both sides, so the written hour is the stated hour in every
            // process zone; only the locale's hour cycle decides the form.
            return formatter.dateTime(new Date(Date.UTC(2000, 0, 1, hours, minutes)), {
                hour: 'numeric',
                minute: '2-digit',
                timeZone: 'UTC',
            })
        },
        date: (value, presentation = 'long') =>
            formatter.dateTime(dateFromIso(value) ?? new Date(Number.NaN), {
                month: presentation,
                day: 'numeric',
                year: 'numeric',
            }),
        instant: (value) =>
            formatter.dateTime(new Date(value), {
                month: 'long',
                day: 'numeric',
                year: 'numeric',
                timeZone,
            }),
        country: (value) => countryName(value, locale) ?? value,
        list: (value) => new Intl.ListFormat(locale, { type: 'conjunction' }).format(value),
        money: (value, presentation) => {
            const fare = presentation === 'whole' ? formatWholeFare : formatFare
            if (presentation !== 'native' && !value.fixed) {
                return fare(value.amount, fareDisplay(value))
            }
            const stated = fare(value.amount, ownCurrency(value))
            return convertible(value)
                ? `${stated} (≈ ${formatWholeFare(value.amount, rendered)})`
                : stated
        },
    }
}

/** Money's currency and rate, and an instant's zone, are the decision's; every other kind
 *  formats by the locale alone. A value of either kind read where nothing decided is the
 *  caller's defect, as a value of the wrong kind is, so it throws. */
export function undecidedFormatters(locale: Locale): ValueFormatters {
    const refuse = (kind: string) => () => {
        throw new Error(
            `A ${kind} value was formatted where no page frame decided the request's format in ${locale}`,
        )
    }
    return {
        ...createValueFormatters({
            locale,
            currency: CANONICAL_CURRENCY,
            rate: undefined,
            timeZone: DEFAULT_DISPLAY_TIMEZONE,
        }),
        money: refuse('money'),
        instant: refuse('instant'),
    }
}
